import React, { FC, useEffect, useState } from 'react';
import { Button, Tab, usePermissions, useTranslate } from 'react-admin';
import { useHistory } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import { H2, StatusField, TableCell, TextField as TextFieldComponent, Title } from '~/components';
import { NoPermissions } from '~/components/ra';
import { PaperTabbedShowLayout } from '~/layout';
import RaTabbedShowLayout from '~/theme/overrides/RaTabbedShowLayout';
import { Longdash } from '~/utils';
import formatByteSize from '~/utils/formatByteSize';
import { usePartitionArchiveOverview } from '~/hooks/partitionArchive';
import {
  ArchivingStatus,
  PartitionArchiveIssue,
  PartitionArchiveOverview,
  PartitionedTable,
  PartitionStatus,
} from '~/api/partitionArchive';
import PartitionArchivePartitions from './PartitionArchivePartitions';
import {
  formatNumber,
  formatPartitionFrom,
  formatPartitionTo,
  l,
  partitionsPath,
  statusesNewestFirst,
} from './utils';

type Translate = (key: string, options?: any) => string;

const archivingSeverity: Record<ArchivingStatus['status'], number> = {
  ENABLED: 0,
  DISABLED: 1,
  MISCONFIGURED: 2,
};

const serviceOf = (tableId: string): string => tableId.split('.')[0];

const getCoverage = (table: PartitionedTable, status: PartitionStatus) =>
  table.coverage.find((coverage) => coverage.status === status);

const sumCoverage = (tables: PartitionedTable[], statuses: PartitionStatus[]) =>
  tables
    .flatMap((table) => table.coverage)
    .filter((coverage) => statuses.includes(coverage.status))
    .reduce(
      (sum, coverage) => ({
        count: sum.count + coverage.count,
        rowCount: sum.rowCount + (coverage.rowCount || 0),
        byteSize: sum.byteSize + (coverage.byteSize || 0),
      }),
      { count: 0, rowCount: 0, byteSize: 0 }
    );

const PartitionArchivePage: FC<any> = () => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t: Translate = (key, options) => translate(l(key), options);
  const history = useHistory();

  const hasPermission = permissions?.includes('tenant.partitionArchive.view');

  const [responseTime, setResponseTime] = useState<string | null>(null);
  // shared by both tabs: set from a table row or an alert, then the Partitions tab is opened
  const [table, setTable] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();

  useEffect(() => {
    setResponseTime(moment().format('LL LT'));
  }, []);

  const { data: overview, isLoading, isError } = usePartitionArchiveOverview(!!hasPermission);

  if (!permissions) return null;
  if (!hasPermission) return <NoPermissions />;
  if (isLoading) {
    return (
      <Box p={3} display='flex' justifyContent='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !overview) {
    return (
      <>
        <Title resource='partitionArchive' headline={t('title')} subtitle=' ' />
        <Alert severity='error'>{t('error')}</Alert>
      </>
    );
  }

  const archiveTables = [...overview.tables]
    .filter((partitionedTable) => partitionedTable.retentionPolicy === 'ARCHIVE')
    .sort((a, b) => a.id.localeCompare(b.id));

  const openPartitions = (tableId: string, partitionStatus?: string) => {
    setTable(tableId);
    setStatus(partitionStatus);
    history.push(partitionsPath);
  };

  return (
    <>
      <Title
        resource='partitionArchive'
        headline={t('title')}
        subtitle={t('subtitle', { responseTime })}
      />
      <PartitionArchiveSummary overview={overview} t={t} />
      <PartitionArchiveAlerts issues={overview.issues} t={t} onOpenPartitions={openPartitions} />
      <PaperTabbedShowLayout>
        <Tab label={l('tabs.tables')}>
          <PartitionArchiveTables overview={overview} t={t} onOpenPartitions={openPartitions} />
        </Tab>
        <Tab label={l('tabs.partitions')} path='partitions'>
          <PartitionArchivePartitions
            tables={archiveTables}
            table={table}
            status={status}
            onTableChange={setTable}
            onStatusChange={setStatus}
          />
        </Tab>
      </PaperTabbedShowLayout>
    </>
  );
};

interface SummaryProps {
  overview: PartitionArchiveOverview;
  t: Translate;
}

const PartitionArchiveSummary: FC<SummaryProps> = (props) => {
  const { overview, t } = props;
  const classes = useStyles();
  const byteUnit = (unit: string) => t(`byteUnits.${unit}`);

  // archiving status per service: all ENABLED, or the worst one with its reasons and services
  const statusByService = new Map<string, ArchivingStatus>();
  overview.tables.forEach((partitionedTable) => {
    if (partitionedTable.archiving) {
      statusByService.set(serviceOf(partitionedTable.id), partitionedTable.archiving);
    }
  });
  const archivingStatuses = Array.from(statusByService.values());
  const worstStatus = archivingStatuses.reduce<ArchivingStatus['status'] | undefined>(
    (worst, archiving) =>
      !worst || archivingSeverity[archiving.status] > archivingSeverity[worst]
        ? archiving.status
        : worst,
    undefined
  );
  const worstServices = Array.from(statusByService.entries())
    .filter(([, archiving]) => archiving.status === worstStatus)
    .map(([service]) => service);
  const worstReasons = Array.from(
    new Set(
      archivingStatuses
        .filter((archiving) => archiving.status === worstStatus && archiving.reason)
        .map((archiving) => archiving.reason as string)
    )
  );

  const archived = sumCoverage(overview.tables, ['DROPPED']);
  const enabledTables = overview.tables.filter((partitionedTable) => {
    return partitionedTable.archiving?.status === 'ENABLED';
  });
  const inProgress =
    sumCoverage(overview.tables, ['PENDING', 'EXPORTING', 'UPLOADED']).count +
    sumCoverage(enabledTables, ['EXPIRED']).count;
  const attentionPartitions = overview.issues
    .filter((issue) => issue.type !== 'SERVICE_UNAVAILABLE')
    .reduce((sum, issue) => sum + (issue.count || 0), 0);
  const unavailableServices = overview.issues.filter(
    (issue) => issue.type === 'SERVICE_UNAVAILABLE'
  ).length;

  const partitions = (count: number) =>
    t('summary.partitions', { smart_count: count, count: formatNumber(count) });

  return (
    <>
      <H2 topSpacing={0} bottomSpacing={1}>
        {t('summary.title')}
      </H2>
      <Paper className={classes.summary}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextFieldComponent label={t('summary.archiving')} copyable={false} textWrap>
              {worstStatus ? (
                <div>
                  <StatusField status={worstStatus} />
                  {worstStatus !== 'ENABLED' && (
                    <div className={classes.reason}>
                      {worstReasons.length > 0
                        ? t('summary.reasonOfServices', {
                            reason: worstReasons.join('; '),
                            services: worstServices.join(', '),
                          })
                        : t('summary.services', { services: worstServices.join(', ') })}
                    </div>
                  )}
                </div>
              ) : (
                Longdash
              )}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextFieldComponent label={t('summary.archived')} copyable={false} textWrap>
              {t('summary.archivedValue', {
                partitions: partitions(archived.count),
                rows: t('summary.rows', {
                  smart_count: archived.rowCount,
                  count: formatNumber(archived.rowCount),
                }),
                size: formatByteSize(archived.byteSize, byteUnit),
              })}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextFieldComponent label={t('summary.inProgress')} copyable={false} textWrap>
              {partitions(inProgress)}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextFieldComponent label={t('summary.needsAttention')} copyable={false} textWrap>
              {unavailableServices > 0
                ? t('summary.needsAttentionValue', {
                    partitions: partitions(attentionPartitions),
                    services: t('summary.servicesUnavailable', {
                      smart_count: unavailableServices,
                    }),
                  })
                : partitions(attentionPartitions)}
            </TextFieldComponent>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

interface AlertsProps {
  issues: PartitionArchiveIssue[];
  t: Translate;
  onOpenPartitions: (tableId: string, partitionStatus?: string) => void;
}

const PartitionArchiveAlerts: FC<AlertsProps> = (props) => {
  const { issues, t, onOpenPartitions } = props;
  const classes = useStyles();

  if (issues.length === 0) return null;

  return (
    <div className={classes.alerts}>
      {issues.map((issue, index) => {
        const options = {
          smart_count: issue.count || 0,
          count: formatNumber(issue.count || 0),
          table: issue.table,
          service: issue.service,
          reason: issue.reason || Longdash,
        };
        switch (issue.type) {
          case 'SERVICE_UNAVAILABLE':
            return (
              <Alert key={index} severity='warning' className={classes.alert}>
                {t('alerts.SERVICE_UNAVAILABLE', options)}
              </Alert>
            );
          case 'ARCHIVE_FAILED':
            return (
              <Alert
                key={index}
                severity='error'
                className={classes.alert}
                action={
                  <Button
                    label={l('alerts.viewFailed')}
                    onClick={() => onOpenPartitions(issue.table as string, 'FAILED')}
                  />
                }
              >
                {t('alerts.ARCHIVE_FAILED', options)}
              </Alert>
            );
          case 'RETAINED_NO_STORAGE':
            return (
              <Alert
                key={index}
                severity='error'
                className={classes.alert}
                action={
                  <Button
                    label={l('alerts.viewPartitions')}
                    onClick={() => onOpenPartitions(issue.table as string)}
                  />
                }
              >
                {t('alerts.RETAINED_NO_STORAGE', options)}
              </Alert>
            );
          default:
            // an issue type this version does not know: not shown, rather than with a wrong text
            return null;
        }
      })}
    </div>
  );
};

interface TablesProps {
  overview: PartitionArchiveOverview;
  t: Translate;
  onOpenPartitions: (tableId: string) => void;
}

const PartitionArchiveTables: FC<TablesProps> = (props) => {
  const { overview, t, onOpenPartitions } = props;
  const classes = useStyles();
  const byteUnit = (unit: string) => t(`byteUnits.${unit}`);

  if (overview.tables.length === 0) {
    return <div style={RaTabbedShowLayout.content}>{t('tables.empty')}</div>;
  }

  const tables = [...overview.tables].sort((a, b) => a.id.localeCompare(b.id));

  return (
    <div style={RaTabbedShowLayout.content}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('tables.table')}</TableCell>
            <TableCell>{t('tables.retention')}</TableCell>
            <TableCell>{t('tables.policy')}</TableCell>
            <TableCell>{t('tables.archiving')}</TableCell>
            <TableCell>{t('tables.coverage')}</TableCell>
            <TableCell>{t('tables.period')}</TableCell>
            <TableCell align='right'>{t('tables.archivedRows')}</TableCell>
            <TableCell align='right'>{t('tables.archivedSize')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tables.map((partitionedTable) => {
            const dropped = getCoverage(partitionedTable, 'DROPPED');
            const sortedCoverage = [...partitionedTable.coverage].sort(
              (a, b) =>
                statusesNewestFirst.indexOf(a.status) - statusesNewestFirst.indexOf(b.status)
            );
            return (
              <TableRow key={partitionedTable.id}>
                <TableCell>
                  {/* only ARCHIVE tables have a partition list: underlined link, as PricingDatagrid */}
                  {partitionedTable.retentionPolicy === 'ARCHIVE' ? (
                    <Link
                      component='button'
                      type='button'
                      underline='always'
                      onClick={() => onOpenPartitions(partitionedTable.id)}
                      className={classes.tableLink}
                    >
                      {partitionedTable.id}
                    </Link>
                  ) : (
                    partitionedTable.id
                  )}
                </TableCell>
                <TableCell>{partitionedTable.retention}</TableCell>
                <TableCell>{t(`tables.policies.${partitionedTable.retentionPolicy}`)}</TableCell>
                <TableCell>
                  {partitionedTable.archiving ? (
                    <>
                      <StatusField status={partitionedTable.archiving.status} />
                      {partitionedTable.archiving.reason && (
                        <div className={classes.reason}>{partitionedTable.archiving.reason}</div>
                      )}
                    </>
                  ) : (
                    Longdash
                  )}
                </TableCell>
                {/* one line per status in both cells, in the same order, so a status and its period align */}
                <TableCell>
                  {sortedCoverage.length > 0
                    ? sortedCoverage.map((coverage) => (
                        <div key={coverage.status} className={classes.coverage}>
                          <StatusField status={coverage.status} />
                          <span>
                            {t('tables.coverageCount', { count: formatNumber(coverage.count) })}
                          </span>
                        </div>
                      ))
                    : Longdash}
                </TableCell>
                <TableCell>
                  {sortedCoverage.length > 0
                    ? sortedCoverage.map((coverage) => (
                        <div key={coverage.status} className={classes.coverage}>
                          {t('periodValue', {
                            from: formatPartitionFrom(coverage.from),
                            to: formatPartitionTo(coverage.to),
                          })}
                        </div>
                      ))
                    : Longdash}
                </TableCell>
                <TableCell align='right'>
                  {dropped?.rowCount != null ? formatNumber(dropped.rowCount) : Longdash}
                </TableCell>
                <TableCell align='right'>
                  {dropped?.byteSize != null
                    ? formatByteSize(dropped.byteSize, byteUnit)
                    : Longdash}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  summary: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  reason: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  alerts: {
    marginBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(1),
  },
  // a button styled as a link (no browser "open in new tab"): the text of the cell, not the button font
  tableLink: {
    font: 'inherit',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  coverage: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
    height: 20,
  },
}));

export default PartitionArchivePage;
